import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useApproveKnowledgeProposal, useKnowledgeProposal } from '../../hooks/useKnowledge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { KindBadge } from './KindBadge'

const PROPOSAL_LINK = /\/knowledge\?tab=proposals&proposal=([0-9a-f-]{36})/gi

/** The knowledge proposals an assistant answer links to. */
export function proposalUidsIn(text: string | null | undefined, org: string): string[] {
    if (!text) return []
    const out = new Set<string>()
    for (const m of text.matchAll(PROPOSAL_LINK)) {
        if (text.slice(0, m.index).endsWith(`/${org}`)) out.add(m[1].toLowerCase())
    }

    return [...out]
}

/**
 * A proposal the assistant filed, as a card in its answer (spec 2026-11-01-01):
 * *Approve* in one click for whoever may decide it, *Open* for the review.
 */
export function AssistantKnowledgeProposalCard({ org, uid }: { org: string; uid: string }) {
    const { t } = useTranslation('knowledge')
    const { data: proposal } = useKnowledgeProposal(org, uid)
    const approve = useApproveKnowledgeProposal(org)
    if (!proposal) return null
    const pending = proposal.status === 'pending'

    return (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card p-2" data-testid="assistant-knowledge-proposal">
            <div className="flex min-w-0 items-center gap-1.5 text-sm">
                <AppIcon path={SECTION_ICONS.knowledge} size="xs" className="shrink-0" aria-hidden />
                <span className="text-xs text-muted-foreground">{t(`proposals.action.${proposal.action}`)}</span>
                <span className="truncate font-medium">{proposal.title}</span>
                <KindBadge kind={proposal.kind} />
                {!pending && <span className="text-xs text-muted-foreground">{t(`proposals.status.${proposal.status}`)}</span>}
            </div>
            <div className="flex gap-1">
                <Button variant="ghost" size="sm" asChild>
                    <Link
                        to="/$org/knowledge"
                        params={{ org }}
                        search={{ tab: 'proposals', proposal: uid }}
                        data-testid="assistant-knowledge-proposal-open"
                    >
                        {t('assistant_card.open')}
                    </Link>
                </Button>
                {pending && proposal.can_decide && (
                    <Button
                        size="sm"
                        disabled={approve.isPending}
                        onClick={() => approve.mutate({ uid })}
                        data-testid="assistant-knowledge-proposal-approve"
                    >
                        {t('proposals.approve')}
                    </Button>
                )}
            </div>
        </div>
    )
}
